import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { format } from "date-fns";

import {
  setDownloadProgress,
  stopDownloadPolling,
  useDeleteReportsMutation,
  useGetAllReportsQuery,
  useLazyGetDownloadStatusQuery,
} from "@/services";
import { useAppDispatch, useAppSelector } from "@/store";
import { useSnackbar, useEffectiveClientScope } from "@/hooks";
import { roles } from "@/roles";
import { reportColumns } from "@/columns";
import { Fiters } from "./components";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";
import ProcessBox from "../trade-data/ProcessBox";
import { getFilenameFromUrl } from "@/utils";
import { tableCompactSx } from "@/components/table/table-styles";
import Pagination from "@/components/paginations/pagination";
import { tokens } from "@/tokens";

let interval: NodeJS.Timeout | null = null;

type PostImportAdjustmentProps = {
  hideHeader?: boolean;
};

const PostImportAdjustment: FC<PostImportAdjustmentProps> = ({
  hideHeader = false,
}): ReactElement => {
  const { show } = useSnackbar();
  const dispatch = useAppDispatch();
  const {
    isDownloadPollingActive,
    downloadFileId,
    downloadProgressValue,
    activeClientId,
  } = useAppSelector((state) => state.polling);
  const { user } = useAppSelector((state) => state.auth);
  const { clientId, tenantId, isTDAdmin, shouldRequireClientSelection } =
    useEffectiveClientScope();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [filters, setFilters] = useState({
    adjustmentType: "" as "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty",
    startDate: new Date("1 Jan 2000"),
    endDate: new Date(),
  });
  const [ids, setIds] = useState<{ delete: string[]; download: string[] }>({
    delete: [],
    download: [],
  });
  const [hasDownloadedFile, setHasDownloadedFile] = useState<boolean>(false);
  const navigate = useNavigate();
  const progressSubscriberRef = useRef<
    ((options: SubscriptionOptions) => void) | null
  >(null);

  const paramFilters = useMemo(() => {
    return {
      pageNo: page,
      pageSize: limit,
      startDate: format(filters.startDate ?? new Date(), "yyyy-MM-dd"),
      endDate: format(filters.endDate ?? new Date(), "yyyy-MM-dd"),
      ...(filters.adjustmentType && { adjustmentType: filters.adjustmentType }),
      clientId,
      ...(isTDAdmin && tenantId && { tenantId }),
      onlyAdjustments: true,
    };
  }, [page, limit, filters, clientId, isTDAdmin, tenantId]);

  const { data: apiData, isFetching } = useGetAllReportsQuery(
    { ...paramFilters },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldRequireClientSelection,
    }
  );

  const [
    fetchDownloadProgress,
    { data: downloadProgress, error: downloadError },
  ] = useLazyGetDownloadStatusQuery({
    pollingInterval: 5000,
  });

  const [deleteReport] = useDeleteReportsMutation();

  const { data: reports = [], meta } = apiData ?? {};
  const totalReports = meta?.countDocs ?? 0;

  const hasCreateAccess = useMemo(() => {
    return (
      !!user &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDCLIENT, roles.TDADMIN].includes(user.userRole)
    );
  }, [user]);

  const handleEnhancedReconciliation = () => {
    navigate("/compliance/create-adjustment");
  };

  const deleteReportAction = async (report: IReport) => {
    const { _id } = report;

    setIds({ delete: [_id], download: [...ids.download] });
    const isDeleted = await deleteReport(_id).unwrap();
    setIds({ delete: [], download: [...ids.download] });

    if (isDeleted) {
      show(
        "Successfully deleted!",
        `Report with id ${_id} deleted sucessfully!`,
        "success"
      );
    }
  };

  const pullProgress = useCallback(
    async (fileId: string) => {
      const progressSubscriber = fetchDownloadProgress(fileId);

      progressSubscriberRef.current =
        progressSubscriber.updateSubscriptionOptions;
    },
    [fetchDownloadProgress]
  );

  // Restart polling if exited by page changing
  useEffect(() => {
    if (isDownloadPollingActive && downloadFileId) {
      pullProgress(downloadFileId);
    }
  }, [isDownloadPollingActive, downloadFileId, pullProgress]);

  useEffect(() => {
    if (
      progressSubscriberRef.current &&
      (downloadError ||
        downloadProgress?.status === "SUCCESS" ||
        downloadProgress?.status === "ERROR")
    ) {
      progressSubscriberRef.current({ pollingInterval: 0 });
      progressSubscriberRef.current = null;
      dispatch(stopDownloadPolling());
    }
  }, [downloadProgress, downloadError, dispatch]);

  useEffect(() => {
    if (interval) {
      clearInterval(interval);
    }

    if (
      downloadError ||
      downloadProgress?.status === "SUCCESS" ||
      downloadProgress?.status === "FAILED"
    ) {
      dispatch(setDownloadProgress(100));
      return;
    }

    if (downloadProgressValue >= 90) {
      return;
    }

    if (downloadProgress?.status === "PENDING" && downloadProgressValue < 90) {
      interval = setInterval(() => {
        const randomIncrement = Math.floor(Math.random() * 6) + 5;

        dispatch(
          setDownloadProgress(
            Math.min(downloadProgressValue + randomIncrement, 100)
          )
        );
      }, 20000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [downloadProgressValue, downloadProgress, downloadError, dispatch]);

  const downloadFile = async (signedUrl: string) => {
    try {
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new Error("Download failed!");
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = getFilenameFromUrl(signedUrl);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      show("Successfully downloaded", ``, "success");
      setHasDownloadedFile(true);
    } catch (error) {
      if (error instanceof Error) {
        show("Failed to download file", error.message, "error");
      }
    }
  };

  // Legacy download action - kept for backward compatibility
  const downloadFileAction = async (_report: IReport) => {
    // No-op - downloads are handled by reportColumns
  };

  const actions = {
    delete: deleteReportAction,
    download: downloadFileAction,
  };

  const handleActions = (type: "download" | "delete", report: IReport) => {
    actions[type](report);
  };

  const columns = reportColumns({
    action: handleActions,
    actionsLoading: {
      ...ids,
    },
  });

  return (
    <Stack sx={{ height: "100%", minHeight: 0, flex: 1, flexDirection: "column" }}>
      {/* Header + Filters */}
      <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 1.5, bgcolor: tokens.bg.muted, borderBottom: `1px solid ${tokens.border.default}` }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" gap={1.5}>
          {!hideHeader && (
            <Typography color={tokens.text.primary} fontSize={24} fontWeight={tokens.fontWeight.semibold}>
              Post Import Adjustment{" "}
              <Typography
                component="span"
                color={tokens.text.muted}
                fontSize={24}
                fontWeight={tokens.fontWeight.normal}
              >
                ({totalReports})
              </Typography>
            </Typography>
          )}
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            sx={{
              justifyContent: "flex-end",
              ml: "auto",
            }}
          >
            <Fiters onFiltersChange={(filterUpdate) => {
              setFilters({
                ...filters,
                adjustmentType: filterUpdate.adjustmentType,
                startDate: filterUpdate.startDate,
                endDate: filterUpdate.endDate,
              });
            }} />
            {hasCreateAccess && (
              <Button
                variant="contained"
                startIcon={<IoMdAdd />}
                sx={{
                  fontWeight: tokens.fontWeight.bold,
                  fontSize: tokens.fontSize.md,
                  borderRadius: tokens.radius.md,
                  height: 36,
                  px: 2,
                  boxShadow: 'none',
                  bgcolor: tokens.brand.primary,
                  '&:hover': { bgcolor: tokens.brand.primaryHover },
                }}
                onClick={handleEnhancedReconciliation}
              >
                Create Adjustment
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* ProcessBox */}
      {((activeClientId === clientId &&
        downloadProgress?.status === "PENDING") ||
        downloadProgress?.msg) &&
        !hasDownloadedFile && (
          <Box sx={{ px: 3, pb: 1 }}>
            <ProcessBox
              title={downloadProgress.msg}
              progress={downloadProgressValue}
              {...(downloadProgress?.url && {
                hideLoader: true,
                renderSubtitle: () => (
                  <span>
                    Click here to download the{" "}
                    <Box
                      component="b"
                      onClick={() => downloadFile(downloadProgress?.url ?? "")}
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                    >
                      file
                    </Box>
                  </span>
                ),
              })}
            />
          </Box>
        )}

      {/* Table section */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Stack sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          mx: 3,
          mb: 2,
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          overflow: "hidden",
          ...tableCompactSx,
        }}>
          {(isFetching || shouldRequireClientSelection) && reports.length === 0 ? (
            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: tokens.radius.lg }} />
          ) : (
            <>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
                {isFetching && reports.length > 0 && (
                  <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%", minWidth: { xs: 800, md: "100%" } }}>
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key as string}
                          align={(col.align as any) || "left"}
                          sx={{ width: col.width, minWidth: col.minWidth }}
                        >
                          {col.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.length === 0 && !isFetching ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center" sx={{ py: 8, border: 'none' }}>
                          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 'calc(100vh - 350px)' }}>
                            <Box
                              sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                bgcolor: `${tokens.brand.primary}08`,
                                border: `2px dashed ${tokens.brand.primary}30`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <IoMdAdd size={32} color={`${tokens.brand.primary}80`} />
                            </Box>
                            <Box sx={{ maxWidth: 360, textAlign: 'center' }}>
                              <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                                No Adjustments Yet
                              </Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, lineHeight: 1.5 }}>
                                Create prior disclosure adjustments or reconciliation adjustments to manage your post-import duty corrections.
                              </Typography>
                            </Box>
                            {hasCreateAccess && (
                              <Button
                                variant="contained"
                                startIcon={<IoMdAdd />}
                                onClick={handleEnhancedReconciliation}
                                sx={{
                                  fontWeight: tokens.fontWeight.bold,
                                  fontSize: tokens.fontSize.sm2,
                                  borderRadius: tokens.radius.md,
                                  height: 36,
                                  px: 2.5,
                                  boxShadow: 'none',
                                  bgcolor: tokens.brand.primary,
                                  '&:hover': { bgcolor: tokens.brand.primaryHover },
                                }}
                              >
                                Create Adjustment
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.map((report: any) => (
                        <TableRow key={report._id}>
                          {columns.map((col) => (
                            <TableCell
                              key={`${report._id}-${col.key}`}
                              align={(col.align as any) || "left"}
                            >
                              {col.component?.(report)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination — inside container */}
              {totalReports > 0 && (
                <Pagination
                  page={page}
                  limit={limit}
                  total={totalReports}
                  onPageChange={setPage}
                  onLimitChange={(l) => { setLimit(l); setPage(1); }}
                />
              )}
            </>
          )}
        </Stack>
      </Box>

    </Stack>
  );
};

export default PostImportAdjustment;
